import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import sortBy from 'lodash.sortby';
import env from '../../utils/config';

interface IJobRow {
  name: string;
  time: string;
  status: string;
}

interface IJobStatuses {
  [key: string]: {
    status: string;
  };
}

export function JobManager() {
  const [rows, setRows] = useState<IJobRow[]>([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [response, setResponse] = useState('');

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => {
    setModalOpen(false);
    setResponse('');
  };

  useEffect(() => {
    async function getJobsStatus() {
      try {
        const response = await fetch(`${env.SERVER_URL}/api/v1/job/status`);
        const json = await response.json();
        const formattedData = await formatJobsData(json);
        setRows(formattedData);
      } catch (err) {
        console.error(err);
        setResponse('Error loading jobs, please try refreshing');
        handleOpen();
      }
    }
    getJobsStatus();
  }, []);

  const formatJobsData = async (jobObj: IJobStatuses) => {
    const arr = [];
    for (const job in jobObj) {
      const { status } = jobObj[job];
      if (status === 'running') {
        arr.push({
          name: job,
          time: 'currently running',
          status: 'pending'
        });
      } else {
        const jobHistory = await fetch(`${env.SERVER_URL}/api/v1/feed/get-feed-history?limit=1&import_id=${job}`);
        const json = await jobHistory.json();
        if (json.data && json.data.length > 0) {
          const timestamp = json.data[0].updatedAt;
          const date = new Date(Date.parse(timestamp));
          arr.push({
            name: job,
            time: date.toLocaleString(),
            status: json.data[0].job_status
          });
        } else {
          arr.push({
            name: job,
            time: 'unknown',
            status: 'unknown'
          });
        }
      }
    }
    // alphabetize
    return sortBy(arr, ['name']);
  };

  const runJob = async (job: string) => {
    try {
      const apiKey = process.env.REACT_APP_API_KEY;
      const response = await fetch(`${env.IMPORT_URL}/apip/v1/job/${job}?apiKey=${apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      const json = await response.json();
      setResponse(json.message ? json.message : json);
    } catch (err) {
      console.error(err);
      setResponse('Error running job, please try again');
    }
  }

  const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: 'darkgray',
      color: '#FFFFFF',
      fontWeight: 'bold',
      textTransform: 'uppercase',
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
      padding: '4px 16px',
    },
  }));

  /*ts-lint ignore no-any*/
  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
  }));

  return (
    <>
      <div className="App">
        <Modal
          open={modalOpen}
          onClose={handleClose}
        >
          <div className="confirmationModal">
            {response ?
              <>
                <p>{response}</p>
              </>
              :
              <>
                <p>
                  Are you sure you want to run the {selectedJob} job?
                </p>
                <Button className="modalButton" variant="contained" onClick={() => {runJob(selectedJob);}}>Yes</Button>
                <Button className="modalButton" variant="contained" onClick={() => {handleClose();}}>No</Button>
              </>
            }
          </div>
        </Modal>
        {rows.length === 0 ?
          <CircularProgress className="loadingCircle" />
          :
          <>
            <h3> RETAIL API JOBS MANAGER </h3>
            <Table sx={{ minWidth: 700, maxWidth: 1200 }}>
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell>Job name</StyledTableCell>
                  <StyledTableCell>Last run time</StyledTableCell>
                  <StyledTableCell>Last job status</StyledTableCell>
                  <StyledTableCell/>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <StyledTableRow key={row.name}>
                    <StyledTableCell component="th" scope="row">
                      {row.name}
                    </StyledTableCell>
                    <StyledTableCell>{row.time}</StyledTableCell>
                    <StyledTableCell
                      className={`${row.status === 'success' ? 'font-awesome-check' : (row.status === 'failure' ? 'font-awesome-cross' : '')}`}>
                        {row.status}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Button
                        disabled={row.status === 'pending'}
                        variant="contained"
                        onClick={() => {
                          setSelectedJob(row.name);
                          handleOpen();
                        }}
                      >
                        Run Job
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </>
        }
      </div>
    </>
  )
}
